/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import { CreditCard, Radio, Receipt, User, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import useSettings from "@/hooks/useSettings";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * SplitCardPaymentConfirmDialog displays a confirmation and amount input modal before dispatching
 * payment for a specific split payment item.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {(amount: number) => void} props.onConfirm
 * @param {boolean} props.isLoading
 * @param {object} props.payment - Active split payment object
 * @param {number} props.maxAmount - Maximum remaining due amount for this split
 * @param {object} props.orderData - Active order data
 */
export default function SplitCardPaymentConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  payment,
  maxAmount = 0,
  orderData,
}) {
  const { settings } = useSettings();
  const hasPaxTerminal = settings?.has_pax_terminal === true;

  const [enteredAmount, setEnteredAmount] = useState("");
  const splitName = payment?.name || "Split Payment";

  useEffect(() => {
    if (isOpen) {
      setEnteredAmount(maxAmount > 0 ? Number(maxAmount).toFixed(2) : "");
    }
  }, [isOpen, maxAmount]);

  const parsedAmount = parseFloat(enteredAmount);
  const isValidAmount = Number.isFinite(parsedAmount) && parsedAmount > 0 && parsedAmount <= maxAmount + 0.001;

  const handleQuickFill = (amount) => {
    setEnteredAmount(Number(amount).toFixed(2));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[440px] bg-cblack4 border-gray-800 text-white p-6 shadow-2xl animate-fadeIn">
        <DialogHeader className="flex flex-col justify-center items-center gap-3 text-center">
          {/* Animated Cyan Glow Card Icon */}
          <div className="relative flex justify-center items-center h-13 w-13 rounded-2xl bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 p-3 shadow-inner">
            <CreditCard className="h-7 w-7" />
            {hasPaxTerminal && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-500" />
              </span>
            )}
          </div>

          <DialogTitle className="text-white text-lg font-bold tracking-wide">
            {hasPaxTerminal ? `Pay ${splitName} with Card Terminal?` : `Confirm Card Payment for ${splitName}?`}
          </DialogTitle>

          <DialogDescription className="text-cwhite3 text-xs leading-relaxed max-w-[320px]">
            {hasPaxTerminal ? (
              <>
                Enter the amount to charge on the connected card terminal for <strong>{splitName}</strong>.
              </>
            ) : (
              <>
                Enter the card amount to record for <strong>{splitName}</strong>.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Editable Amount Input Box */}
        <div className="mt-2 space-y-3">
          <div className="bg-cblack3 p-4 rounded-xl border border-gray-800 flex flex-col items-center justify-center">
            <div className="flex justify-between items-center w-full mb-1.5 px-1">
              <span className="text-[10px] text-cwhite3 uppercase tracking-wider font-semibold">
                Amount to Charge
              </span>
              <span className="text-[11px] text-cwhite3">
                Max: <strong className="text-white">£{Number(maxAmount).toFixed(2)}</strong>
              </span>
            </div>

            <div className="relative w-full">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-cyellow text-xl font-bold font-sans">
                £
              </span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={maxAmount}
                value={enteredAmount}
                onChange={(e) => setEnteredAmount(e.target.value)}
                placeholder="0.00"
                disabled={isLoading}
                autoFocus
                className="w-full pl-8 pr-4 py-2.5 bg-cblack2 border border-gray-700 focus:border-cyan-500 rounded-lg text-cyellow text-2xl font-extrabold font-sans outline-none text-center shadow-inner"
              />
            </div>

            {/* Quick Fill Preset Buttons */}
            <div className="flex gap-2 mt-2.5 w-full">
              <button
                type="button"
                onClick={() => handleQuickFill(maxAmount)}
                className="flex-1 py-1 px-2 text-[11px] font-semibold bg-cblack2 hover:bg-gray-800 text-cyan-400 border border-cyan-500/30 rounded-md transition"
              >
                Full Due (£{Number(maxAmount).toFixed(2)})
              </button>
              {maxAmount >= 10 && (
                <button
                  type="button"
                  onClick={() => handleQuickFill(Math.min(maxAmount, 10))}
                  className="py-1 px-2.5 text-[11px] font-semibold bg-cblack2 hover:bg-gray-800 text-cwhite2 border border-gray-700 rounded-md transition"
                >
                  £10
                </button>
              )}
              {maxAmount >= 20 && (
                <button
                  type="button"
                  onClick={() => handleQuickFill(Math.min(maxAmount, 20))}
                  className="py-1 px-2.5 text-[11px] font-semibold bg-cblack2 hover:bg-gray-800 text-cwhite2 border border-gray-700 rounded-md transition"
                >
                  £20
                </button>
              )}
            </div>
          </div>

          {/* Quick Meta Row */}
          <div className="bg-cblack3 p-3 rounded-xl border border-gray-800 space-y-2 text-xs">
            <div className="flex justify-between items-center pb-2 border-b border-gray-800/60">
              <span className="text-cwhite3 flex items-center gap-1.5">
                <Receipt className="h-3.5 w-3.5 text-cyan-400" />
                Order Reference
              </span>
              <span className="font-semibold text-white font-mono">
                Order #{orderData?.id}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-cwhite3 flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-cyellow" />
                Split Target
              </span>
              <span className="font-medium text-white">
                {splitName}
              </span>
            </div>

            {payment?.tipAmount > 0 && (
              <div className="flex justify-between items-center pt-1 border-t border-gray-800/40 text-[11px]">
                <span className="text-cwhite3">Includes Tip</span>
                <span className="font-semibold text-cgreen">
                  +£{Number(payment.tipAmount).toFixed(2)}
                </span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <div className="grid grid-cols-2 gap-3 w-full pt-1">
            <Button
              type="button"
              variant="outline"
              className="bg-cblack3 hover:bg-cblack7 border-gray-800 text-cwhite3 hover:text-white font-medium py-2 rounded-lg transition"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              isLoading={isLoading}
              disabled={isLoading || !isValidAmount}
              className={`bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-2 rounded-lg shadow-md transition ${(!isValidAmount || isLoading) ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={() => {
                if (isValidAmount) {
                  onConfirm?.(parsedAmount);
                }
              }}
            >
              {hasPaxTerminal ? "Dispatch Payment" : "Confirm Card Payment"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
